import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { FaMapMarkerAlt, FaStar, FaEnvelope, FaPhone, FaEdit, FaSpinner, FaSave } from 'react-icons/fa';
import { SkeletonProfile } from '../components/Skeleton';
import { toast } from 'react-toastify';
import api from '../services/api';
import SEOHead from '../components/SEOHead';
import WorkerLocationSelect, {
  useWorkerLocationOptions,
  WorkerLocationOption,
} from '../components/WorkerLocationSelect';

interface ProfileData {
  id: number;
  email?: string;
  first_name: string;
  last_name?: string;
  last_initial?: string;
  role: string;
  phone_number?: string;
  bio: string;
  avatar: string;
  location: string;
  skills: string[];
  rating: number;
  review_count: number;
  created_at: string;
  is_public?: boolean;
  phone_verified_at?: string | null;
  sms_booking_available?: boolean;
}

interface ProfileEditData {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  bio: string;
  location: string;
  skills: string[];
}

type VerificationFeedback = {
  kind: 'success' | 'error';
  message: string;
};

const toEditData = (profile: ProfileData): ProfileEditData => ({
  firstName: profile.first_name || '',
  lastName: profile.last_name || '',
  phoneNumber: profile.phone_number || '',
  bio: profile.bio || '',
  location: profile.location || '',
  skills: profile.skills || [],
});

const getApiErrorMessage = (error: unknown, fallback: string) => {
  const message = (error as { response?: { data?: { message?: unknown } } })
    ?.response?.data?.message;
  return typeof message === 'string' && message.trim() ? message : fallback;
};

const ProfilePage = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useSelector((state: RootState) => state.auth);
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [skillInput, setSkillInput] = useState('');
  const [pendingLocation, setPendingLocation] = useState<WorkerLocationOption | null>(null);
  const [editData, setEditData] = useState<ProfileEditData>({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    bio: '',
    location: '',
    skills: [] as string[],
  });

  const [isPublic, setIsPublic] = useState(false);
  const [togglingVisibility, setTogglingVisibility] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [codeRequested, setCodeRequested] = useState(false);
  const [requestingCode, setRequestingCode] = useState(false);
  const [confirmingCode, setConfirmingCode] = useState(false);
  const [verificationFeedback, setVerificationFeedback] = useState<VerificationFeedback | null>(null);

  const isOwnProfile = user?.id === Number(id);
  const {
    options: workerLocationOptions,
    loading: workerLocationsLoading,
    error: workerLocationsError,
  } = useWorkerLocationOptions(editing && isOwnProfile && profile?.role === 'worker');
  const originalEditData = useRef(editData);
  const profileRequestId = useRef(0);

  // Track whether the form has unsaved changes
  const hasUnsavedChanges = editing && (
    editData.firstName !== originalEditData.current.firstName ||
    editData.lastName !== originalEditData.current.lastName ||
    editData.phoneNumber !== originalEditData.current.phoneNumber ||
    editData.bio !== originalEditData.current.bio ||
    (profile?.role === 'worker'
      ? pendingLocation !== null
      : editData.location !== originalEditData.current.location) ||
    JSON.stringify(editData.skills) !== JSON.stringify(originalEditData.current.skills)
  );

  // Warn on browser close/refresh when there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    setVerificationCode('');
    setCodeRequested(false);
    setVerificationFeedback(null);
    fetchProfile();
    return () => {
      profileRequestId.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user?.id]);

  const fetchProfile = async (showLoading = true) => {
    const requestId = profileRequestId.current + 1;
    profileRequestId.current = requestId;
    if (showLoading) {
      setLoading(true);
      setProfile(null);
    }
    setEditing(false);
    setPendingLocation(null);
    try {
      const res = await api.get(`/users/${id}`);
      if (profileRequestId.current !== requestId) return false;

      setProfile(res.data);
      const data = toEditData(res.data);
      setEditData(data);
      originalEditData.current = data;
      setIsPublic(res.data.is_public || false);
      return true;
    } catch (err) {
      if (profileRequestId.current !== requestId) return false;

      if (showLoading) setProfile(null);
      console.error('Failed to load profile:', err);
      toast.error('Failed to load profile');
      return false;
    } finally {
      if (showLoading && profileRequestId.current === requestId) {
        setLoading(false);
      }
    }
  };

  const handleSave = async () => {
    const phoneChanged = editData.phoneNumber.trim()
      !== originalEditData.current.phoneNumber.trim();
    setSaving(true);
    try {
      const profileUpdate = {
        firstName: editData.firstName,
        lastName: editData.lastName,
        phoneNumber: editData.phoneNumber,
        bio: editData.bio,
        skills: editData.skills,
        ...(profile?.role === 'worker' ? {} : { location: editData.location }),
      };
      const res = await api.put(`/users/${id}`, {
        ...profileUpdate,
        ...(pendingLocation ? {
          locationSelection: {
            locality: pendingLocation.locality,
            region: pendingLocation.region,
          },
        } : {}),
      });
      setProfile(res.data);
      const savedData = toEditData(res.data);
      setEditData(savedData);
      originalEditData.current = savedData;
      setPendingLocation(null);
      setEditing(false);
      if (phoneChanged) {
        setVerificationCode('');
        setCodeRequested(false);
        if (res.data.phone_number && !res.data.phone_verified_at) {
          setVerificationFeedback({
            kind: 'success',
            message: res.data.sms_booking_available === true
              ? 'Phone saved. Verify it before receiving booking texts.'
              : 'Phone saved. Text verification and booking texts are not available yet.',
          });
        } else {
          setVerificationFeedback(null);
        }
      }
      toast.success('Profile updated!');
    } catch (err) {
      console.error('Failed to update profile:', err);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleRequestVerificationCode = async () => {
    setRequestingCode(true);
    setVerificationFeedback(null);
    try {
      const res = await api.post(`/users/${id}/phone-verification/request`, {});
      if (res.data?.message === 'Phone number is already verified.') {
        const refreshed = await fetchProfile(false);
        setCodeRequested(false);
        setVerificationFeedback({
          kind: refreshed ? 'success' : 'error',
          message: refreshed
            ? 'Phone number is already verified.'
            : 'Phone status could not be refreshed. Please reload and try again.',
        });
        return;
      }

      setCodeRequested(true);
      setVerificationFeedback({
        kind: 'success',
        message: 'Verification code sent. Enter the six-digit code from the text message.',
      });
    } catch (err) {
      setVerificationFeedback({
        kind: 'error',
        message: getApiErrorMessage(err, 'Could not send a verification code. Please try again.'),
      });
    } finally {
      setRequestingCode(false);
    }
  };

  const handleConfirmVerificationCode = async () => {
    if (!/^\d{6}$/.test(verificationCode)) {
      setVerificationFeedback({
        kind: 'error',
        message: 'Enter the six-digit verification code.',
      });
      return;
    }

    setConfirmingCode(true);
    setVerificationFeedback(null);
    try {
      await api.post(`/users/${id}/phone-verification/confirm`, {
        code: verificationCode,
      });
      const refreshed = await fetchProfile(false);
      if (!refreshed) {
        setVerificationFeedback({
          kind: 'error',
          message: 'Phone verified, but the current status could not be refreshed. Please reload the page.',
        });
        return;
      }

      setVerificationCode('');
      setCodeRequested(false);
      setVerificationFeedback({
        kind: 'success',
        message: 'Phone number verified.',
      });
    } catch (err) {
      setVerificationFeedback({
        kind: 'error',
        message: getApiErrorMessage(err, 'Could not verify that code. Please try again.'),
      });
    } finally {
      setConfirmingCode(false);
    }
  };

  const handleToggleVisibility = async () => {
    setTogglingVisibility(true);
    try {
      const next = !isPublic;
      await api.patch(`/workers/${id}/visibility`, { is_public: next });
      setIsPublic(next);
      toast.success(next
        ? 'Your profile is now visible to signed-in employers'
        : 'Your profile is now hidden from employer search');
    } catch (err) {
      toast.error('Failed to update visibility');
    } finally {
      setTogglingVisibility(false);
    }
  };

  const addSkill = () => {
    const skill = skillInput.trim().slice(0, 30);
    if (skill && !editData.skills.includes(skill) && editData.skills.length < 20) {
      setEditData(prev => ({ ...prev, skills: [...prev.skills, skill] }));
      setSkillInput('');
    }
  };

  const removeSkill = (skill: string) => {
    setEditData(prev => ({
      ...prev,
      skills: prev.skills.filter(s => s !== skill),
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <SkeletonProfile />
        </div>
      </div>
    );
  }

  const hasPrivateProfileAccess = Boolean(
    profile
    && user
    && (user.id === profile.id || user.role === 'admin'),
  );

  if (!profile || (profile.email && !hasPrivateProfileAccess)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <h2 className="text-2xl font-bold text-gray-700">User not found</h2>
      </div>
    );
  }

  const displayLastName = profile.last_name || profile.last_initial || '';
  const displayName = `${profile.first_name} ${displayLastName}`.trim();

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead title="My Profile — StageCrew" description="View and manage your StageCrew profile." />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded shadow overflow-hidden">
          {/* Header */}
          <div className="bg-blue-600 text-white p-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-6">
                <div className="w-24 h-24 rounded-full bg-blue-800 flex items-center justify-center text-3xl font-bold" role="img" aria-label={`${displayName} avatar`}>
                  {profile.first_name?.charAt(0) || '?'}{displayLastName.charAt(0) || '?'}
                </div>
                <div>
                  <h1 className="text-3xl font-bold">
                    {displayName}
                  </h1>
                  <p className="text-blue-200 capitalize">{profile.role}</p>
                  {profile.location && (
                    <p className="flex items-center mt-1">
                      <FaMapMarkerAlt className="mr-1" /> {profile.location}
                    </p>
                  )}
                </div>
              </div>
              {isOwnProfile && profile.email && !editing && (
                <button
                  onClick={() => setEditing(true)}
                  className="bg-white text-blue-600 px-4 py-2 rounded font-medium hover:bg-gray-100 flex items-center"
                >
                  <FaEdit className="mr-2" /> Edit Profile
                </button>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="p-8">
            {editing ? (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                    <input
                      type="text"
                      value={editData.firstName}
                      onChange={e => setEditData(prev => ({ ...prev, firstName: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                    <input
                      type="text"
                      value={editData.lastName}
                      onChange={e => setEditData(prev => ({ ...prev, lastName: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="profile-phone-number" className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                  <input
                    id="profile-phone-number"
                    type="tel"
                    inputMode="tel"
                    value={editData.phoneNumber}
                    onChange={e => setEditData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                    placeholder="+12125551234"
                    maxLength={20}
                    aria-describedby="profile-phone-format"
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p id="profile-phone-format" className="mt-2 text-xs text-gray-500">
                    Use E.164 format: + followed by country code and number (for example, +12125551234).
                  </p>
                </div>

                {profile.role === 'worker' ? (
                  <WorkerLocationSelect
                    id="profile-worker-location"
                    label="Location"
                    options={workerLocationOptions}
                    selected={pendingLocation}
                    onChange={setPendingLocation}
                    currentLocation={editData.location}
                    loading={workerLocationsLoading}
                    error={workerLocationsError}
                    preserveCurrent
                  />
                ) : (
                  <div>
                    <label htmlFor="profile-location" className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                    <input
                      id="profile-location"
                      type="text"
                      value={editData.location}
                      onChange={e => setEditData(prev => ({ ...prev, location: e.target.value }))}
                      className="min-h-[44px] w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Bio</label>
                  <textarea
                    value={editData.bio}
                    onChange={e => setEditData(prev => ({ ...prev, bio: e.target.value }))}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Skills</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={skillInput}
                      onChange={e => setSkillInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSkill(); } }}
                      placeholder="Add a skill"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button type="button" onClick={addSkill} className="bg-gray-200 px-4 py-2 rounded hover:bg-gray-300">
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {editData.skills.map((skill, i) => (
                      <span key={i} className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm flex items-center">
                        {skill}
                        <button type="button" onClick={() => removeSkill(skill)} className="ml-2 text-blue-600 hover:text-blue-800">&times;</button>
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:opacity-50 flex items-center"
                  >
                    {saving ? <FaSpinner className="animate-spin mr-2" /> : <FaSave className="mr-2" />}
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    onClick={() => {
                      if (hasUnsavedChanges) {
                        if (!window.confirm('You have unsaved changes. Discard them?')) return;
                      }
                      setEditData(originalEditData.current);
                      setPendingLocation(null);
                      setEditing(false);
                    }}
                    className="bg-gray-200 text-gray-700 px-6 py-2 rounded hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  {hasUnsavedChanges && (
                    <span className="text-sm text-amber-600 font-medium">Unsaved changes</span>
                  )}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="md:col-span-2">
                  <div className="mb-6">
                    <h2 className="text-xl font-bold mb-3">About</h2>
                    <p className="text-gray-700">{profile.bio || 'No bio provided.'}</p>
                  </div>

                  {profile.skills && profile.skills.length > 0 && (
                    <div className="mb-6">
                      <h2 className="text-xl font-bold mb-3">Skills</h2>
                      <div className="flex flex-wrap gap-2">
                        {profile.skills.map((skill, i) => (
                          <span key={i} className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <h2 className="text-xl font-bold mb-3">Reviews</h2>
                    <Link to={`/ratings/${id}`} className="text-blue-600 hover:underline">
                      View all reviews ({profile.review_count || 0})
                    </Link>
                  </div>
                </div>

                <div>
                  <div className="bg-gray-50 p-6 rounded">
                    <h3 className="text-lg font-bold mb-4">Details</h3>
                    <div className="space-y-3 text-sm">
                      {profile.rating > 0 && (
                        <div className="flex items-center">
                          <FaStar className="text-yellow-500 mr-2" />
                          <span>{profile.rating.toFixed(1)} ({profile.review_count} reviews)</span>
                        </div>
                      )}
                      {profile.email && (
                        <div className="flex items-center">
                          <FaEnvelope className="text-gray-400 mr-2" />
                          <span>{profile.email}</span>
                        </div>
                      )}
                      {profile.phone_number && (
                        <div className="flex items-center">
                          <FaPhone className="text-gray-400 mr-2" />
                          <span>{profile.phone_number}</span>
                        </div>
                      )}
                      {isOwnProfile && profile.role === 'worker' && profile.phone_number && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <div className="flex items-center justify-between gap-3">
                            <p className="text-xs font-medium text-gray-700">Phone verification</p>
                            <span
                              className={`text-xs font-semibold ${
                                profile.phone_verified_at ? 'text-green-700' : 'text-amber-700'
                              }`}
                            >
                              {profile.phone_verified_at ? 'Verified' : 'Not verified'}
                            </span>
                          </div>

                          {!profile.phone_verified_at && (
                            <div className="mt-3 space-y-3">
                              {profile.sms_booking_available === true ? (
                                <>
                                  <p id="phone-verification-help" className="text-xs text-gray-500">
                                    Verify this number to receive StageCrew booking texts.
                                  </p>
                                  <button
                                    type="button"
                                    onClick={handleRequestVerificationCode}
                                    disabled={requestingCode || confirmingCode}
                                    className="w-full text-sm px-3 py-2 rounded font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                                  >
                                    {requestingCode ? 'Requesting...' : codeRequested ? 'Send another code' : 'Request code'}
                                  </button>
                                </>
                              ) : (
                                <p id="phone-verification-help" className="text-xs text-gray-500">
                                  Text verification is temporarily unavailable. StageCrew booking texts are not active yet.
                                </p>
                              )}

                              {profile.sms_booking_available === true && codeRequested && (
                                <div className="space-y-2">
                                  <label htmlFor="phone-verification-code" className="block text-xs font-medium text-gray-700">
                                    Six-digit verification code
                                  </label>
                                  <input
                                    id="phone-verification-code"
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    pattern="[0-9]{6}"
                                    maxLength={6}
                                    value={verificationCode}
                                    onChange={event => setVerificationCode(
                                      event.target.value.replace(/\D/g, '').slice(0, 6),
                                    )}
                                    aria-describedby="phone-verification-help"
                                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                  <button
                                    type="button"
                                    onClick={handleConfirmVerificationCode}
                                    disabled={confirmingCode}
                                    className="w-full text-sm px-3 py-2 rounded font-medium bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                                  >
                                    {confirmingCode ? 'Confirming...' : 'Confirm code'}
                                  </button>
                                </div>
                              )}
                            </div>
                          )}

                          {verificationFeedback && (
                            <p
                              role={verificationFeedback.kind === 'error' ? 'alert' : 'status'}
                              aria-live={verificationFeedback.kind === 'error' ? 'assertive' : 'polite'}
                              aria-atomic="true"
                              className={`mt-3 text-xs ${
                                verificationFeedback.kind === 'error' ? 'text-red-700' : 'text-green-700'
                              }`}
                            >
                              {verificationFeedback.message}
                            </p>
                          )}
                        </div>
                      )}
                      <div className="text-gray-500 mt-4">
                        Member since {new Date(profile.created_at).toLocaleDateString()}
                      </div>
                      {isOwnProfile && profile.role === 'worker' && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <p className="text-xs font-medium text-gray-700 mb-2">Profile Visibility</p>
                          <button
                            onClick={handleToggleVisibility}
                            disabled={togglingVisibility}
                            className={`w-full text-sm px-3 py-2 rounded font-medium transition-colors ${
                              isPublic
                                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                          >
                            {togglingVisibility
                              ? 'Saving...'
                              : isPublic
                                ? 'Visible to signed-in employers'
                                : 'Hidden from employer search'}
                          </button>
                          <p className="text-xs text-gray-400 mt-1 text-center">
                            {isPublic
                              ? 'Only signed-in employers and StageCrew admins can view it.'
                              : 'Signed-in employers cannot find or view it.'}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default ProfilePage;
